/**
 * BrainstormOverlayHost — hand-written shadcn/Tailwind JSX drawer+modal shell
 * for the brainstorm surface.
 *
 * Restored 2026-04-20 from the BlockHost-mounted cogo JSON variant (PR #133
 * Phase 3 Step 3.9 scaffold). The 4 island components (CandidateRail,
 * CandidatePreview, Narrative, ObservationSink) keep their existing React
 * implementations; this file re-assembles the layout that previously lived
 * in `blocks-v2/studio/brainstorm-overlay/ui.cogo.json`.
 *
 * Preserved behavior:
 * - Right-side drawer (420px wide, `fixed` overlay) bound to
 *   `useStudioStore.brainstorm.drawerOpen`.
 * - Modal backdrop + panel shown when `modalExpandedId` is non-empty.
 * - NarrativeIsland + ObservationSinkIsland mounted unconditionally (they
 *   self-gate on the same store state).
 * - Gap 2 durable saveObservations on drawer close (true→false edge) —
 *   same wiring as PR #157 + PR #187 workerBaseUrl activation.
 */
import { useEffect, useMemo, useRef } from 'react'
import { X } from 'lucide-react'

import { RenderContext, type RenderContextValue } from '@cogo/react-renderer'
import { getSharedRegistry } from '@cogo/react-renderer-v2'

import { useStudioStore } from '@/store/studio-store'
import { useKg } from '@/features/design-system/kg-provider'

import { CandidateRailIsland } from '@/features/brainstorm/islands/candidate-rail'
import { CandidatePreviewIsland } from '@/features/brainstorm/islands/candidate-preview'
import { NarrativeIsland } from '@/features/brainstorm/islands/narrative'
import { ObservationSinkIsland } from '@/features/brainstorm/islands/observation-sink'

export function BrainstormOverlayHost() {
  const drawerOpen = useStudioStore((s) => s.brainstorm.drawerOpen)
  const modalExpandedId = useStudioStore((s) => s.brainstorm.modalExpandedId)
  const activeCandidateId = useStudioStore((s) => s.brainstorm.activeCandidateId)
  const loading = useStudioStore((s) => s.brainstorm.loading)
  const errorMessage = useStudioStore((s) => s.brainstorm.errorMessage)
  const dwellStartMs = useStudioStore((s) => s.brainstorm.dwellStartMs)

  const collapse = useStudioStore((s) => s.brainstorm.collapse)
  const dismissAll = useStudioStore((s) => s.brainstorm.dismissAll)

  const { repo: kg } = useKg()
  const prevDrawerOpen = useRef(drawerOpen)

  // Minimal RenderContext for the candidate preview. CandidatePreviewIsland
  // mounts BlockV2, whose downstream v1 renderers (container, text, etc.)
  // call useRenderContext() and throw when no provider is in scope.
  // The previous BlockHost-mounted overlay supplied one implicitly; this
  // hand-written host must provide it explicitly. Preview-only: resolveSymbol
  // is identity, dispatchEvent is a no-op (picker doesn't execute flows).
  const previewCtx = useMemo<RenderContextValue>(
    () => ({
      registry: getSharedRegistry(),
      resolveSymbol: (expr: string) => expr,
      dispatchEvent: () => {},
      mode: 'preview',
    }),
    [],
  )

  // Gap 2 — drawer close triggers durable save of pending observations.
  // Fires exactly once on the true → false transition.
  useEffect(() => {
    const wasOpen = prevDrawerOpen.current
    prevDrawerOpen.current = drawerOpen
    if (!wasOpen || drawerOpen) return
    if (!kg) return
    void kg.saveObservations().then(
      (r) => {
        if (r.data.failed.length > 0) {
          const allFailed = r.data.committed === 0
          if (allFailed) {
            console.warn(
              '[brainstorm] saveObservations: all observations failed to commit — ' +
                'likely VITE_KG_WORKER_URL is unset or unreachable. Set ' +
                'VITE_KG_WORKER_URL in .env.development (or pass workerBaseUrl to ' +
                'LocalWasmRepository) and reload. Observations remain in ' +
                'localStorage + pending queue.',
              { failed: r.data.failed.length },
            )
          } else {
            console.warn(
              `[brainstorm] saveObservations: ${r.data.failed.length} observations could not be committed — kept locally`,
              r.data.failed,
            )
          }
        }
      },
      (err) => {
        console.warn('[brainstorm] saveObservations on drawer close failed', err)
      },
    )
  }, [drawerOpen, kg])

  const modalOpen = modalExpandedId !== ''

  return (
    <div className="pointer-events-none fixed inset-0 z-50">
      {drawerOpen && (
        <aside
          className="pointer-events-auto absolute top-0 right-0 h-full w-[420px] border-l border-border bg-background shadow-xl flex flex-col"
          role="dialog"
          aria-label="Brainstorm"
        >
          <header className="flex items-center justify-between p-3 border-b border-border shrink-0">
            <h2 className="text-sm font-semibold">Brainstorm</h2>
            <button
              type="button"
              onClick={() => dismissAll()}
              aria-label="Close brainstorm drawer"
              className="w-6 h-6 rounded flex items-center justify-center hover:bg-foreground/5 text-muted-foreground transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </header>

          {errorMessage !== '' && (
            <div className="mx-3 my-2 rounded-md border border-destructive/30 bg-destructive/5 p-2 text-xs text-destructive shrink-0">
              {errorMessage}
            </div>
          )}

          {loading && (
            <div className="p-4 text-xs text-muted-foreground">Loading candidates…</div>
          )}

          {!loading && errorMessage === '' && (
            <div className="flex-1 min-h-0 overflow-auto">
              <CandidateRailIsland activeId={activeCandidateId} />
            </div>
          )}
        </aside>
      )}

      {modalOpen && (
        <div
          className="pointer-events-auto absolute inset-0 bg-background/80 backdrop-blur-sm"
          onClick={() => collapse()}
          role="presentation"
        >
          <div
            className="absolute inset-8 rounded-lg border border-border bg-background shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <RenderContext.Provider value={previewCtx}>
              <CandidatePreviewIsland expandedId={modalExpandedId} />
            </RenderContext.Provider>
          </div>
        </div>
      )}

      <NarrativeIsland activeId={activeCandidateId} expandedId={modalExpandedId} />
      <ObservationSinkIsland dwellStartMs={dwellStartMs} />
    </div>
  )
}
